/*
  NUDIENT: Filmen i FeaturePair, paket D3, M-090 tillägget. Plan:
  nudient/PLAN-D3-featurepair.md. Laddas av sections/nudient-feature-pair.liquid,
  bara när det valda paret har en film. Ett undantag från M-090 punkt 1, utan
  skript, bara för filmerna, beslutat av Vidar 12 oktober 2026. Används också
  av D2, M-092 punkt 9, och D8, M-095 punkt 1.

  - Filmens stillbild visas tills filmen har laddats och spelar.
  - Filmen hämtas först när bandet närmar sig skärmen, inom en halv skärm.
  - Den spelar när den syns och pausas när den lämnar skärmen.
  - Med minskad rörelse hämtas ingen film, och bara stillbilden visas.
  - En film som kunden har pausat, attributet paused, startar inte igen av
    sig själv, M-095 punkt 1.

  Snabbare filmer, M-095 tillägget, planen för D8 punkt 16:
  - Avståndet räknas mot det som rullar: .page-wrapper från 990, där Horizon
    rullar den i stället för fönstret, assets/base.css rad 28 till 52, och
    fönstret under. Byter skärmen bredd över 990 räknas det om.
  - Stillbilden hämtas med sidan, med låg prioritet, i sektionerna, så att
    rutan aldrig är tom, också när sidan hoppar direkt till filmen.
  - Förinläsningen slås på, preload auto, när filmen ska hämtas, så att
    Safari också hämtar HLS innan filmen spelar.
  - Safari får Shopifys strömmande format, HLS, ur data-hls, M-095
    tillägget punkt 3. Andra webbläsare får den minsta mp4 som är minst så
    bred som rutan gånger skärmens skärpa, ur snippets/nudient-film.liquid.
    Räcker ingen tas den största.

  Filmer som Safari inte låter starta, rättat 14 oktober 2026 efter Vidars
  prov, planen för G1 punkt 24:
  - Safari på Mac i energisparläge, och webbläsare som stoppar all
    automatisk uppspelning, avvisar play() med NotAllowedError, också för
    ljudlösa filmer. Safari skickar då playing och direkt pause. Filmen
    visas därför först när den verkligen går, vid timeupdate med tiden över
    0 och inte pausad, inte vid playing. Till dess syns stillbilden.
  - En avvisad film märks med blocked och startas igen vid kundens första
    tryck eller tangent på sidan, som webbläsaren då tillåter, om den syns och
    kunden inte har pausat den.
  - muted, defaultMuted och playsInline sätts också som egenskaper innan
    filmen får sin källa. Attributen står redan i snippets/nudient-film.liquid.
  - Knapparna får nudient:film-blocked och nudient:film-playing, så att
    pausknappen visar spela medan filmen står still och paus när den går.

  Knapparna, D8, M-095 punkt 1 och 4: nudient-video-controls styr filmerna
  i sitt område, det närmaste [data-nudient-video]. [data-video-pause]
  pausar och startar dem, [data-video-sound] slår på och av ljudet. Knappens
  namn för skärmläsare står i data-label-pause, data-label-play,
  data-label-sound-on och data-label-sound-off. Utan knappar fungerar
  filmerna som förut.

  Värden som inte är tokens:
  - 50% i rootMargin: en halv skärm före, när filmen börjar hämtas.
  - (min-width: 990px) och .page-wrapper: Horizons gräns och behållare för
    rullningen, som i snippets/nudient-keep-place.liquid.
  - auto och scroll: värdena för overflow-y som betyder att behållaren
    rullar.
  - Apple i navigator.vendor och application/vnd.apple.mpegurl: Safari, och
    att webbläsaren själv spelar HLS. Bara Safari får HLS.
  - auto i preload: filmen hämtas när skriptet väljer den.
  - playing på elementet: filmen spelar, och CSS:en visar den över
    stillbilden. blocked: webbläsaren avvisade uppspelningen.
  - NotAllowedError: webbläsarens namn på en avvisad uppspelning.
  - pointerdown, keydown och touchend: kundens tryck och tangenter, när en
    avvisad film får försöka igen.
  - paused på elementet och data-state på knapparna, paused och sound:
    kundens val, som CSS:en visar med ikonen.
*/

const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
const wide = window.matchMedia('(min-width: 990px)');

/** Det som rullar: .page-wrapper från 990, annars fönstret */
const scroller = () => {
  const wrapper = document.querySelector('.page-wrapper');
  if (!wrapper) return null;
  const overflow = getComputedStyle(wrapper).overflowY;
  return overflow === 'auto' || overflow === 'scroll' ? wrapper : null;
};

/** Safari, som själv spelar Shopifys strömmande format */
/** @param {HTMLVideoElement} video */
const safariHls = (video) => /Apple/.test(navigator.vendor) && video.canPlayType('application/vnd.apple.mpegurl') !== '';

/** Filmer som webbläsaren inte lät starta, och som försöker igen vid kundens första tryck */
const blocked = new Set();
const GESTURES = ['pointerdown', 'keydown', 'touchend'];
const retryBlocked = () => {
  const films = [...blocked];
  blocked.clear();
  GESTURES.forEach((name) => document.removeEventListener(name, retryBlocked, true));
  for (const film of films) film.retry();
};
/** @param {NudientFeatureVideo} film */
const waitForGesture = (film) => {
  if (blocked.size === 0) GESTURES.forEach((name) => document.addEventListener(name, retryBlocked, true));
  blocked.add(film);
};

class NudientFeatureVideo extends HTMLElement {
  /** @type {IntersectionObserver | undefined} */ #near;
  /** @type {IntersectionObserver | undefined} */ #seen;
  #visible = false;
  #loaded = false;
  #abort = new AbortController();

  connectedCallback() {
    const video = this.querySelector('video');
    if (!video) return;
    this.#observe(video);
    wide.addEventListener('change', () => this.#observe(video), { signal: this.#abort.signal });
  }

  disconnectedCallback() {
    this.#near?.disconnect();
    this.#seen?.disconnect();
    this.#abort.abort();
  }

  /** @param {HTMLVideoElement} video */
  #observe(video) {
    this.#near?.disconnect();
    this.#seen?.disconnect();
    const root = scroller();

    if (reduced.matches) return;
    if (this.#loaded) {
      this.#watch(video, root);
      return;
    }
    this.#near = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.#near?.disconnect();
        this.#load(video);
        this.#watch(video, root);
      },
      { root, rootMargin: '50% 0px' }
    );
    this.#near.observe(this);
  }

  /** Safari får HLS, andra den minsta mp4 som räcker */
  /** @param {HTMLVideoElement} video */
  #load(video) {
    this.#loaded = true;
    const shown = () => {
      if (video.paused || video.currentTime <= 0) return;
      this.setAttribute('playing', '');
      video.removeEventListener('timeupdate', shown);
    };
    video.addEventListener('timeupdate', shown);
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.preload = 'auto';
    const hls = video.dataset.hls;
    if (hls && safariHls(video)) {
      video.src = hls;
      video.load();
      return;
    }
    const sources = /** @type {HTMLSourceElement[]} */ ([...video.querySelectorAll('source[data-src]')]);
    const box = this.getBoundingClientRect();
    const scale = window.devicePixelRatio || 1;
    const sized = sources
      .map((source) => ({ source, width: Number(source.dataset.width) || 0, height: Number(source.dataset.height) || 0 }))
      .sort((a, b) => a.width - b.width);
    const enough = sized.find(({ width, height }) => width > 0 && height > 0 && width >= Math.max(box.width, (box.height * width) / height) * scale);
    const chosen = (enough || sized[sized.length - 1])?.source;
    for (const source of sources) {
      if (source === chosen) source.setAttribute('src', source.dataset.src || '');
      else source.remove();
    }
    video.load();
  }

  /** Kundens paus: filmen står still tills kunden startar den igen */
  /** @param {boolean} paused */
  setPaused(paused) {
    this.toggleAttribute('paused', paused);
    const video = this.querySelector('video');
    if (!video) return;
    if (paused) video.pause();
    else if (this.#visible && video.currentSrc) this.#start(video);
  }

  /** Försöker igen efter kundens tryck, om filmen syns och inte är pausad av kunden */
  retry() {
    const video = this.querySelector('video');
    if (video && this.#visible && !this.hasAttribute('paused')) this.#start(video);
  }

  /** play(), och märker filmen när webbläsaren avvisar den */
  /** @param {HTMLVideoElement} video */
  #start(video) {
    video.play().then(
      () => {
        if (!this.hasAttribute('blocked')) return;
        this.removeAttribute('blocked');
        this.dispatchEvent(new CustomEvent('nudient:film-playing', { bubbles: true }));
      },
      (error) => {
        if (error?.name !== 'NotAllowedError') return;
        if (!this.hasAttribute('blocked')) {
          this.setAttribute('blocked', '');
          this.dispatchEvent(new CustomEvent('nudient:film-blocked', { bubbles: true }));
        }
        waitForGesture(this);
      }
    );
  }

  /** Spelar när filmen syns och pausar när den lämnar skärmen */
  /** @param {HTMLVideoElement} video */
  /** @param {Element | null} root */
  #watch(video, root) {
    this.#seen = new IntersectionObserver(
      (entries) => {
        this.#visible = entries.some((entry) => entry.isIntersecting);
        if (this.#visible && !this.hasAttribute('paused')) this.#start(video);
        else video.pause();
      },
      { root }
    );
    this.#seen.observe(this);
  }
}

if (!customElements.get('nudient-feature-video')) {
  customElements.define('nudient-feature-video', NudientFeatureVideo);
}

class NudientVideoControls extends HTMLElement {
  #abort = new AbortController();

  connectedCallback() {
    const { signal } = this.#abort;
    this.querySelector('[data-video-pause]')?.addEventListener('click', (event) => this.#pause(event), { signal });
    this.querySelector('[data-video-sound]')?.addEventListener('click', (event) => this.#sound(event), { signal });
    const scope = this.closest('[data-nudient-video]') || this.parentElement;
    scope?.addEventListener('nudient:film-blocked', () => this.#show('paused'), { signal });
    scope?.addEventListener('nudient:film-playing', () => this.#show('playing'), { signal });
  }

  /** Pausknappens läge när webbläsaren stoppar eller släpper filmen */
  /** @param {'paused' | 'playing'} state */
  #show(state) {
    const button = /** @type {HTMLElement | null} */ (this.querySelector('[data-video-pause]'));
    if (!button) return;
    button.dataset.state = state;
    button.setAttribute('aria-label', (state === 'paused' ? button.dataset.labelPlay : button.dataset.labelPause) || '');
  }

  disconnectedCallback() {
    this.#abort.abort();
  }

  get films() {
    const scope = this.closest('[data-nudient-video]') || this.parentElement;
    return /** @type {NudientFeatureVideo[]} */ ([...(scope?.querySelectorAll('nudient-feature-video') || [])]);
  }

  /** @param {Event} event */
  #pause(event) {
    const button = /** @type {HTMLElement} */ (event.currentTarget);
    const paused = button.dataset.state !== 'paused';
    button.dataset.state = paused ? 'paused' : 'playing';
    button.setAttribute('aria-label', (paused ? button.dataset.labelPlay : button.dataset.labelPause) || '');
    for (const film of this.films) film.setPaused(paused);
  }

  /** @param {Event} event */
  #sound(event) {
    const button = /** @type {HTMLElement} */ (event.currentTarget);
    const on = button.dataset.state !== 'sound';
    button.dataset.state = on ? 'sound' : 'muted';
    button.setAttribute('aria-label', (on ? button.dataset.labelSoundOff : button.dataset.labelSoundOn) || '');
    for (const film of this.films) {
      const video = film.querySelector('video');
      if (video) video.muted = !on;
    }
  }
}

if (!customElements.get('nudient-video-controls')) {
  customElements.define('nudient-video-controls', NudientVideoControls);
}
